import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';

import Notification from '@js/components/layouts/notification';

import MapRequest from '@js/modules/maps/mapRequest';
import MapInterface from '@js/modules/maps/mapInterface';

import Polyline from '@js/modules/maps/polyline';
import Simplify from '@js/modules/maps/simplify';

import markerStepIcon from '@/images/maps/marker-step.png';
import markerPauseIcon from '@/images/maps/marker-pause-edit.png';
import markerStartIcon from '@/images/maps/marker-start-play.png';
import markerDistanceIcon from '@/images/maps/distance-marker.png';

// import PWAManager from '@js/modules/pwaManager';


export default class BuildMap extends MapInterface {
    _mapModel = {
        // Initial map points
        initialPoints: undefined,

        // Not used for now
        // // activity polyline
        // activityPolylines: [],

        // activity points
        linePoints: [],

        // all direction arrows
        directionArrows: [],

        // all ghost points
        ghostPoints: [],

        // all distance Markers
        distanceMarkers: [],

        // Marker ID currently dragging
        draggingMarkerId: null,

        // Prevent double click on marker and on map at the same time
        markerEventClicked: false,

        // undo actions
        snapShotStack: [],

        // redo actions (states popped by undo; cleared on any new edit)
        redoStack: [],

        // If a pop-up is already opened
        hasPopup: false,

        // Editing ride ID
        rideId: undefined,

        // Edition mode (can add points and lines)
        editMode: true,

        // callback functions for edit
        mapEdited: [],

        // callback functions for reset
        mapReset: [],

        // distance for callback functions (in km)
        distance: 0,

        // Slope color mode
        slopePercentageEnabled: false,

        // Surface color mode
        surfaceTypeEnabled: false,

        // view point for checking a specific point on map (ex. chart hover)
        viewPointMarker: undefined,

        // Distance updated
        distanceUpdated: undefined,

        // Start point changed
        startPointChanged: undefined,

        // Total duration
        durationSeconds: 0,

        // Per-section track colours: [{from, to, color}] (indices into linePoints)
        sections: [],

        // Callbacks fired when a track segment is clicked while in section-color mode
        sectionClicked: [],

        // Retrace callbacks — unused since the retrace tool was removed (kept for a quick re-enable)
        // retraceDone: [],

        // Keypoint currently selected for keyboard fine-adjust (nudge); null when none
        selectedKeypointIndex: null,

        // One-time hint shown the first time a keypoint is selected for nudging
        nudgeHintShown: false
    };

    constructor(modules, params) {
        params.mapMarkers = [
            {
                id: 'ghost-marker',
                url: markerStepIcon,
                properties: {
                    opacity: 0,
                    size: 0.5
                }
            },
            {
                id: 'step-marker',
                url: markerStepIcon,
                properties: {
                    size: 0.7
                }
            },
            {
                id: 'pause-marker',
                url: markerPauseIcon,
                properties: {
                    size: 0.7
                }
            },
            // {
            //     id: 'distance-marker',
            //     url: `${window.settings.assets_url}/maps/distance-marker.png`,
            //     properties: {
            //         size: 1,
            //         opacity: 1
            //     }
            // },
            {
                id: 'start-marker',
                url: markerStartIcon,
                properties: {
                    size: 0.5
                }
            }
        ];

        super(modules, params);

        // This map is editable: POIs can be added, edited and dragged
        this._model.poiEditable = true;

        let isRideSharedOrCopied = null;

        if (params.initialPoints) {
            this._mapModel.initialPoints = params.initialPoints;

            if (this.hasModule('RIDE_STORE') && this.storeRide) {
                this.storeRide();
            }
        } else {
            if (this.hasModule('RIDE_SHARE') && this.getSharedRide) {
                isRideSharedOrCopied = this.getSharedRide((mode, rideName, rideType, analysis) => {
                    if (mode === 'share') {
                        Notification.info(I18n.t('js.maps.helpers.shared_ride', {name: rideName}));
                    } else if (mode === 'copied') {
                        Notification.info(I18n.t('js.maps.helpers.copied_ride', {name: rideName}));
                    }

                    const technicity = analysis?.technicity
                        ? {
                            level:        analysis.technicity.level,
                            maxGrade:     analysis.technicity.max_grade,
                            unpavedRatio: analysis.technicity.unpaved_ratio
                        }
                        : null;

                    if (rideType || analysis?.level || technicity) {
                        window.dispatchEvent(new CustomEvent('mapplanner:ride-loaded', {
                            detail: {rideType, level: analysis?.level || null, technicity}
                        }));
                    }
                });
            }

            if (!isRideSharedOrCopied && params.reusePreviousTracks && this.hasModule('RIDE_STORE') && this.getStoredRide) {
                this._mapModel.initialPoints = this.getStoredRide();

                if (this._mapModel.initialPoints && this._mapModel.initialPoints.length > 0) {
                    Notification.info(I18n.t('js.maps.helpers.stored_ride'));
                }
            }
        }

        if (Utils.isPresent(params.editMode)) {
            this._mapModel.editMode = params.editMode;
        }

        if (Utils.isPresent(params.rideId)) {
            this._mapModel.rideId = params.rideId;
        }

        if (Utils.isPresent(params.initialSections)) {
            this._mapModel.sections = params.initialSections;
        } else if (params.reusePreviousTracks && this.hasModule('RIDE_STORE') && this.getStoredSections) {
            this._mapModel.sections = this.getStoredSections();
        }

        // Persisted per-segment surface groups (restored onto linePoints once the track is loaded).
        if (Utils.isPresent(params.initialSurfaces)) {
            this._mapModel.initialSurfaces = params.initialSurfaces;
        }

        this._mapModel.$styleButtonContainers = this._model.$mapId.querySelector('#map-controls-container');

        if (this.hasModule('TOOLBAR') && this.initializeToolbar) {
            this.initializeToolbar();
            if ((!this._mapModel.initialPoints || this._mapModel.initialPoints.length === 0) && !isRideSharedOrCopied) {
                this.disableToolbarButtons();
            }
        }

        this.onMapLoaded(() => {
            this.addMapTrackLine(this._model.mapTrackLineName, {offRoadOverlay: true});

            this._mapInitialSetup();

            if (this.hasModule('ELEVATION_COMPUTATION') && this.createElevationMap) {
                this.createElevationMap();
            }

            if (this._mapModel.editMode) {
                this.onMapEdited(() => {
                    this._drawDirectionArrows();
                    // this._drawGhostPoints();
                });
            }

            if (params.highlightedPlaceName) {
                this.highlightPlaceName(params.initialLocation[0], params.initialLocation[1], params.highlightedPlaceName);
            } else if (params.useSearchMarker && params.initialLocation) {
                this.displaySearchMarker(params.initialLocation[0], params.initialLocation[1]);
            }

            // On the home the onboarding tour carries this guidance; elsewhere (planner,
            // collaborative) surface it as a transient notification.
            if (this._model.mapId !== 'home-map') {
                Notification.info(window.screen.width < 1024
                    ? I18n.t('js.maps.helpers.mobile.first_point')
                    : I18n.t('js.maps.helpers.desktop.first_point'));
            }

            if (this._mapModel.rideId && this.hasModule('RIDE_POIS') && this.fetchRidePOIs) {
                this.fetchRidePOIs(this._mapModel.rideId);
            }

            this._model.map.on('zoomstart', () => {
                this._model.previousZoom = this._model.map.getZoom();
            });

            this._model.map.on('zoomend', () => {
                this._saveMapPosition();

                if (!this.isDataChangedOnZoom(this._model.map.getZoom(), this._model.previousZoom)) {
                    return;
                }

                this._redrawKeyPoints({refresh: true});
                this._drawDirectionArrows();
            });

            if (this._mapModel.editMode) {
                // Don't add point if double click or double tapping
                this._model.map.doubleClickZoom.enable();

                // Add a listener for the click event
                this._model.map.on('click', this._addKeyPointDebounce);

                this._model.map.on('dblclick', () => this._model.isDoubleClick = true);

                this._addKeyPointEventHandlers();
            }
        });
    }

    goToLocation = (bounds, center, name, {
        useStartMarker = false,
        showPositionMarker = false
    } = {}) => {
        if (!this._model.map) {
            return;
        }

        // Use first bounds then center
        let lat;
        let lng;
        bounds = this._buildBounds(bounds);

        if (Utils.is().isObject(center)) {
            lat = center.lat;
            lng = center.lng;
        } else {
            lat = center[1];
            lng = center[0];
        }

        if ((!lat || !lng) && !bounds) {
            return;
        }

        // Use search marker as starting point
        if (useStartMarker && !this._model.isComputingRoad && !this._mapModel.linePoints.length) {
            if (this.hasModule('TOOLBAR') && this.activateToolbarButtons) {
                this.activateToolbarButtons();
            }

            if (this.hasModule('ISOCHRONE') && this.computeIsochrone) {
                this.computeIsochrone([lat, lng]);
            }

            this._pushUndoSnapShot();

            this._mapModel.linePoints.push(new MapInterface.PointInfo({
                lat,
                lng
            }, null));

            this._redrawKeyPoints();
            this._mapEditedFuncs();

            // this._model.startingPointAddress = this._model.$locationSearch.val().split(', ').slice(0, 2).join(', ');
        } else if (name) {
            this.highlightPlaceName(lat, lng, name);
        } else if (showPositionMarker) {
            this.displaySearchMarker(lat, lng);
        }

        if ((bounds && (bounds._sw || bounds._ne)) || Array.isArray(bounds)) {
            this.fitBounds(bounds, {padding: 50});
        } else if (center) {
            this.flyTo({
                lat,
                lng
            }, useStartMarker ? 16 : this._model.searchZoom);
        }
    };

    onDistanceUpdated = (callback) => {
        this._mapModel.distanceUpdated = callback;
    };

    onStartPointChanged = (callback) => {
        this._mapModel.startPointChanged = callback;
    };

    onMapEdited = (callback) => {
        this._mapModel.mapEdited.push(callback);
    };

    backToHome = () => {
        if (this._model.isComputingRoad || this._mapModel.linePoints.length < 2) {
            return;
        }

        this._pushUndoSnapShot();

        const firstLinePointCoordinates = this._mapModel.linePoints[0].latLng;

        this._addKeyPointAtClickLoc(firstLinePointCoordinates);

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }

        if (this.hasModule('TOOLBAR')) {
            this.disableBackToHome();
        }
    };

    reverseRoute = () => {
        if (this._model.isComputingRoad || this._mapModel.linePoints.length < 2) {
            return;
        }

        this._pushUndoSnapShot();

        const count = this._mapModel.linePoints.length;
        const reversed = [];
        for (let i = count - 1; i >= 0; i--) {
            const point = this._mapModel.linePoints[i];
            reversed.push(new MapInterface.PointInfo(point.latLng, null, {
                computed: point.computed,
                elevation: point.elevation,
                surface: point.surface
            }));
        }

        this._mapModel.linePoints = reversed;

        // Mirror the section colour ranges so they keep covering the same legs after the flip.
        this._mapModel.sections = this._mapModel.sections.map((section) => ({
            from: count - 1 - section.to,
            to: count - 1 - section.from,
            color: section.color
        }));
        this._persistSections();

        if (this._mapModel.startPointChanged) {
            this._mapModel.startPointChanged(this._mapModel.linePoints[0].latLng);
        }

        this._commitRouteMutation();
    };

    outAndBack = () => {
        if (this._model.isComputingRoad || this._mapModel.linePoints.length < 2) {
            return;
        }

        this._pushUndoSnapShot();

        const outboundLength = this._mapModel.linePoints.length;
        for (let i = outboundLength - 2; i >= 0; i--) {
            const point = this._mapModel.linePoints[i];
            this._mapModel.linePoints.push(new MapInterface.PointInfo(point.latLng, null, {
                computed: i !== 0,
                elevation: point.elevation,
                surface: point.surface
            }));
        }

        // The mirrored return leg shifts every index: drop section colours rather than mis-colour.
        this._clearSectionsSilently();

        this._commitRouteMutation();
    };

    // Shared tail of the structural route mutations (reverse / out-and-back / retrace): reset the
    // road-compute offset and the nudge selection (indices moved), then redraw + persist.
    _commitRouteMutation = () => {
        this._model.previousRoadComputedPoints = 0;
        this._mapModel.selectedKeypointIndex = null;

        this._redrawKeyPoints();
        this._mapEditedFuncs();

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    // Drop section colours without an extra redraw (the caller redraws right after).
    _clearSectionsSilently = () => {
        if (!this._mapModel.sections.length) {
            return;
        }

        this._mapModel.sections = [];
        this.setSectionColorMode(this._model.mapTrackLineName, false);
        this._persistSections();
    };

    getSections = () => {
        return this._mapModel.sections;
    };

    getSurfaceBreakdown = () => {
        this._applyStoredSurfacesOnce();

        const totals = {};
        let total = 0;

        for (let i = 1; i < this._mapModel.linePoints.length; i++) {
            const curr = this._mapModel.linePoints[i];
            const prev = this._mapModel.linePoints[i - 1];
            const distance = curr.deltaDistance || this.distHaversine(prev.latLng, curr.latLng, MapInterface.COORDINATES_PRECISION);
            const group = MapInterface.getSurfaceGroup(curr.surface || prev.surface);

            totals[group] = (totals[group] || 0) + distance;
            total += distance;
        }

        if (!total) {
            return [];
        }

        return Object.keys(totals)
            .map((group) => ({
                group,
                distance: totals[group],
                percent: Math.round(totals[group] / total * 100)
            }))
            .sort((a, b) => b.distance - a.distance);
    };

    // Consecutive-run encoding of the surface GROUP along the track: [{from, to, surface}], indices
    // into linePoints (mirrors sections). Skipped entirely when no real surface data is present.
    getSurfaceRanges = () => {
        // Only persist surfaces when the surface view is active at save time (like a user choice).
        // When off, return undefined so the form sends nothing and the stored column is preserved.
        if (!this._mapModel.surfaceTypeEnabled) {
            return undefined;
        }

        const points = this._mapModel.linePoints;
        if (!points || points.length < 2) {
            return [];
        }

        const ranges = [];
        let start = 0;
        let currentGroup = MapInterface.getSurfaceGroup(points[0].surface);

        for (let i = 1; i < points.length; i++) {
            const group = MapInterface.getSurfaceGroup(points[i].surface);
            if (group !== currentGroup) {
                ranges.push({from: start, to: i - 1, surface: currentGroup});
                start = i;
                currentGroup = group;
            }
        }
        ranges.push({from: start, to: points.length - 1, surface: currentGroup});

        // Nothing worth persisting when the whole track is 'unknown' (no surface data).
        if (ranges.length === 1 && ranges[0].surface === 'unknown') {
            return [];
        }

        return ranges;
    };

    // Restore persisted surface groups onto the (freshly loaded) linePoints, once. Maps each group
    // back to a representative raw surface so the existing colouring/breakdown logic works unchanged.
    _applyStoredSurfacesOnce = () => {
        if (this._surfacesApplied || !this._mapModel.initialSurfaces || !this._mapModel.linePoints.length) {
            return;
        }

        // Map a stored surface GROUP back to a representative raw surface that getSurfaceGroup()
        // re-derives to the same group. Derived from the canonical SURFACE_GROUPS so the two can't drift.
        const groupToSurface = (group) => {
            const config = MapInterface.SURFACE_GROUPS[group];
            return (config && config.surfaces[0]) || '';
        };

        this._mapModel.initialSurfaces.forEach((range) => {
            const surface = groupToSurface(range.surface);
            for (let i = range.from; i <= range.to && i < this._mapModel.linePoints.length; i++) {
                if (this._mapModel.linePoints[i]) {
                    this._mapModel.linePoints[i].surface = surface;
                }
            }
        });

        this._surfacesApplied = true;
    };

    getStageSummary = () => {
        const points = this._mapModel.linePoints;
        if (points.length < 2) {
            return [];
        }

        const overnightPois = [];
        Object.keys(this._poiModel?.poiData || {})
            .forEach((rideId) => {
                (this._poiModel.poiData[rideId] || []).forEach((poi) => {
                    if (poi.overnight && poi.latitude && poi.longitude) {
                        overnightPois.push(poi);
                    }
                });
            });

        if (!overnightPois.length) {
            return [];
        }

        const boundaries = overnightPois
            .map((poi) => this._nearestLinePointIndex({
                lat: poi.latitude,
                lng: poi.longitude
            }))
            .filter((index) => index !== null && index > 0 && index < points.length - 1)
            .sort((a, b) => a - b);

        if (!boundaries.length) {
            return [];
        }

        const ranges = [];
        let start = 0;
        boundaries.forEach((boundary) => {
            ranges.push([start, boundary]);
            start = boundary;
        });
        ranges.push([start, points.length - 1]);

        return ranges.map(([from, to], index) => {
            let distance = 0;
            let elevationGain = 0;

            for (let i = from + 1; i <= to; i++) {
                distance += points[i].deltaDistance || this.distHaversine(points[i - 1].latLng, points[i].latLng, MapInterface.COORDINATES_PRECISION);

                const prevElevation = points[i - 1].elevation;
                const currentElevation = points[i].elevation;
                if (typeof prevElevation === 'number' && typeof currentElevation === 'number' && currentElevation > prevElevation) {
                    elevationGain += currentElevation - prevElevation;
                }
            }

            return {
                day: index + 1,
                distance,
                elevationGain
            };
        });
    };

    onSectionClick = (callback) => {
        this._mapModel.sectionClicked.push(callback);
    };

    toggleSectionColorMode = (enabled) => {
        this._model.sectionColorMode = enabled;

        if (!this._model.map) {
            return;
        }

        const canvas = this._model.map.getCanvas();
        if (canvas) {
            canvas.style.cursor = enabled ? 'pointer' : '';
        }

        if (enabled && !this._sectionClickHandler) {
            this._sectionClickHandler = (event) => {
                if (!this._model.sectionColorMode) {
                    return;
                }

                event.originalEvent?.stopPropagation();

                const index = this._nearestLinePointIndex(event.lngLat);
                if (index === null) {
                    return;
                }

                const range = this._enclosingManualRange(index);
                if (!range) {
                    return;
                }

                const point = this._model.map.project(event.lngLat);
                this._sectionClickedFuncs({
                    from: range.from,
                    to: range.to,
                    x: point.x,
                    y: point.y,
                    color: this._sectionColorAt(range.from, range.to)
                });
            };

            // Hover preview: highlight the leg under the cursor so it's obvious which section will be
            // recoloured before clicking.
            this._sectionHoverHandler = (event) => {
                if (!this._model.sectionColorMode) {
                    return;
                }

                const index = this._nearestLinePointIndex(event.lngLat);
                this._updateSectionHover(index === null ? null : this._enclosingManualRange(index));
            };

            this._sectionLeaveHandler = () => this._updateSectionHover(null);

            [0, 1, 2, 3].forEach((i) => {
                const layer = this._model.mapTrackLineName + '-' + i;
                this._model.map.on('click', layer, this._sectionClickHandler);
                this._model.map.on('mousemove', layer, this._sectionHoverHandler);
                this._model.map.on('mouseleave', layer, this._sectionLeaveHandler);
            });
        }

        // Drop the hover highlight when leaving section-color mode.
        if (!enabled) {
            this._updateSectionHover(null);
        }
    };

    // Overlay a translucent line over the leg [range.from, range.to] (or clear it when range is null),
    // used as the section-color hover preview.
    _updateSectionHover = (range) => {
        const map = this._model.map;
        if (!map) {
            return;
        }

        const sourceId = 'section-hover';
        const coordinates = [];
        if (range) {
            const points = this._mapModel.linePoints;
            for (let i = range.from; i <= range.to && points[i]; i++) {
                coordinates.push([points[i].latLng.lng, points[i].latLng.lat]);
            }
        }

        const data = {
            type:     'Feature',
            geometry: {type: 'LineString', coordinates}
        };

        if (!map.getSource(sourceId)) {
            map.addSource(sourceId, {type: 'geojson', data});
            map.addLayer({
                id:     sourceId,
                type:   'line',
                source: sourceId,
                layout: {'line-cap': 'round', 'line-join': 'round'},
                paint:  {'line-color': '#ffffff', 'line-width': 9, 'line-opacity': coordinates.length ? 0.3 : 0}
            });
        } else {
            map.getSource(sourceId).setData(data);
            map.setPaintProperty(sourceId, 'line-opacity', coordinates.length ? 0.3 : 0);
        }
    };

    setSectionColor = (from, to, color) => {
        if (typeof from !== 'number' || typeof to !== 'number' || to <= from) {
            return;
        }

        this._mapModel.sections = this._mapModel.sections.filter(
            (section) => section.to <= from || section.from >= to
        );

        if (color) {
            this._mapModel.sections.push({
                from,
                to,
                color
            });
        }

        this._persistSections();
        this._reapplySectionColors();
    };

    clearSectionColors = () => {
        this._mapModel.sections = [];

        this._persistSections();
        this._reapplySectionColors();
    };

    /* RETRACE ENGINE — currently unused: the "retrace / head back to a point" tool was removed from
       the toolbar (see MapToolbar / BuildMap). Kept commented out so the feature can be re-enabled
       quickly if needed. (Re-add the toolbar item + wiring + js.maps.toolbar.retrace i18n keys.)
    onRetraceDone = (callback) => {
        this._mapModel.retraceDone.push(callback);
    };

    // One-shot "retrace your steps" mode: the next click on the track picks a turn-around point and
    // the path from the end back to it is mirrored (append the reversed sub-path), then the mode exits.
    toggleRetraceMode = (enabled) => {
        this._model.retraceMode = enabled;

        if (!this._model.map) {
            return;
        }

        const canvas = this._model.map.getCanvas();
        if (canvas) {
            canvas.style.cursor = enabled ? 'pointer' : '';
        }

        if (enabled && !this._retraceClickHandler) {
            this._retraceClickHandler = (event) => {
                if (!this._model.retraceMode) {
                    return;
                }

                event.originalEvent?.stopPropagation();

                const index = this._nearestLinePointIndex(event.lngLat);
                if (index === null) {
                    return;
                }

                this.retraceFromEnd(index);

                // One-shot: leave the mode and let the toolbar reset its toggle
                this._model.retraceMode = false;
                if (canvas) {
                    canvas.style.cursor = '';
                }
                this._retraceDoneFuncs();
            };

            [0, 1, 2, 3].forEach((i) => {
                this._model.map.on('click', this._model.mapTrackLineName + '-' + i, this._retraceClickHandler);
            });
        }
    };

    // Append the reversed sub-path from the finish back to `toIndex` so the route turns around there.
    retraceFromEnd = (toIndex) => {
        const points = this._mapModel.linePoints;
        const lastIndex = points.length - 1;
        if (points.length < 2 || toIndex < 0 || toIndex >= lastIndex) {
            return;
        }

        this._pushUndoSnapShot();

        for (let i = lastIndex - 1; i >= toIndex; i--) {
            const point = points[i];
            points.push(new MapInterface.PointInfo(point.latLng, null, {
                computed: i !== toIndex,
                elevation: point.elevation,
                surface: point.surface
            }));
        }

        // Appending the return leg shifts indices: drop section colours rather than mis-colour.
        this._clearSectionsSilently();

        this._commitRouteMutation();
    };
    */

    applySectionColorsIfEnabled = () => {
        if (!this._mapModel.sections || !this._mapModel.sections.length || !this._mapModel.linePoints.length) {
            return;
        }

        if (this._mapModel.slopePercentageEnabled || this._mapModel.surfaceTypeEnabled) {
            return;
        }

        const sectionGeoJSON = this.buildSectionColorGeoJSON(this._mapModel.linePoints, this._mapModel.sections);
        this.setSectionColorMode(this._model.mapTrackLineName, true, sectionGeoJSON);
    };

    getLinePoints = () => {
        return this._mapModel.linePoints;
    };

    getElevationData = () => {
        if (!this._mapModel.linePoints.length) return null;

        let totalDistance = 0;
        const points = [];

        for (const p of this._mapModel.linePoints) {
            if (p.elevation === null || p.elevation === undefined) continue;

            totalDistance += p.deltaDistance || 0;
            points.push({
                distance: totalDistance / 1000,
                elevation: p.elevation,
                lat: p.latLng.lat,
                lng: p.latLng.lng
            });
        }

        return points.length ? points : null;
    };

    hasActivityData = () => {
        return this._mapModel.linePoints.length > 0;
    };

    hasTimeData = () => {
        return this._mapModel.linePoints.some((point) => point.datetime);
    };

    hasOnlyOnePoint = () => {
        return this._mapModel.linePoints.length === 1;
    };

    recordPoiAdded = () => {
        (this._mapModel.actionHistory ||= []).push('poi');
    };

    onUndoLastPoi = (callback) => {
        this._mapModel.undoPoiCallback = callback;
    };

    forgetLastPoiAdd = () => {
        const history = this._mapModel.actionHistory;
        if (!history) {
            return;
        }

        for (let i = history.length - 1; i >= 0; i--) {
            if (history[i] === 'poi') {
                history.splice(i, 1);
                return;
            }
        }
    };

    hasUndoablePoi = () => {
        const history = this._mapModel.actionHistory;

        return !!(history && history[history.length - 1] === 'poi');
    };

    undo = () => {
        this._mapModel.actionHistory ||= [];
        const history = this._mapModel.actionHistory;
        if (history[history.length - 1] === 'poi') {
            history.pop();

            if (this._mapModel.undoPoiCallback) {
                this._mapModel.undoPoiCallback();
            }

            return;
        }

        if (history[history.length - 1] === 'route') {
            history.pop();
        }

        // Keep the current state so it can be redone.
        this._mapModel.redoStack.push(this._cloneLinePoints());

        if (this._mapModel.snapShotStack.length !== 0) {
            this._revertToSnapshot();
        } else {
            // If no snapshot, it means it's a reused route. So clear the route.
            this.clear();
        }

        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }

        if (this.hasModule('TOOLBAR') && !this.hasActivityData()) {
            this.disableToolbarButtons();
        }

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    clearMap = (callback, {noPopup = false} = {}) => {
        if (!this.hasActivityData()) {
            return;
        }

        if (this.alertPopup && !noPopup) {
            this.alertPopup.fire({
                title: I18n.t('js.maps.clear_map.title'),
                // text: I18n.t('js.maps.clear_map.text'),
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: I18n.t('js.helpers.buttons.cancel'),
                confirmButtonText: I18n.t('js.maps.clear_map.confirm')
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // this.reset();
                        this.clear();
                        this._redrawKeyPoints();
                        this._mapEditedFuncs();

                        if (this.hasModule('TOOLBAR')) {
                            this.disableToolbarButtons();
                        }

                        if (this.hasModule('ISOCHRONE') && this.clearIsochrone) {
                            this.clearIsochrone();
                        }

                        if (this.hasModule('RIDE_POIS') && this.clearAllRidePois) {
                            this.clearAllRidePois();
                        }

                        if (typeof callback === 'function') {
                            callback();
                        }
                    }
                });
        } else {
            this.clear();
            this._redrawKeyPoints();
            this._mapEditedFuncs();

            if (this.hasModule('TOOLBAR')) {
                this.disableToolbarButtons();
            }

            if (this.hasModule('ISOCHRONE') && this.clearIsochrone) {
                this.clearIsochrone();
            }

            if (this.hasModule('ELEVATION_COMPUTATION') && this.clearElevationMap) {
                this.clearElevationMap();
            }

            if (this.hasModule('RIDE_POIS') && this.clearAllRidePois) {
                this.clearAllRidePois();
            }

            if (typeof callback === 'function') {
                callback();
            }
        }
    };

    reset = (action) => {
        if (!Utils.isPresent(this._model.map)) {
            return;
        }

        // just undo everything.
        if (this._mapModel.snapShotStack.length > 0) {
            this._revertToSnapshot(0);
        }

        // The full reset drops every route snapshot, so clear the matching action history too —
        // otherwise a later undo would see orphaned 'route'/'poi' markers with no snapshot behind them.
        this._mapModel.actionHistory = [];

        if (this._mapModel.mapReset.length > 0) {
            for (var i = this._mapModel.mapReset.length - 1; i >= 0; i--) {
                this._mapModel.mapReset[i]();
            }
        }

        this._mapModel.mapReset = [];

        // Call edited callbacks
        if (!(action && action === 'clear')) {
            this._mapEditedFuncs();
        }
        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }

        if (this._mapModel.startPointChanged) {
            this._mapModel.startPointChanged(null);
        }

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    clear = () => {
        if (!Utils.isPresent(this._model.map)) {
            return;
        }

        // kill all the things
        this.reset('clear');
        this._clearKeyPoints({clearAll: true});
        this._removeDistanceMarkers();

        this.resetMapSource(this._model.mapTrackLineName);
        this.resetMapSource(this._model.mapTrackLineName + '-offroad');

        // this._mapModel.activityPolylines = [];
        this._model.previousRoadComputedPoints = 0;
        this._mapModel.keyPoints = [];
        this._mapModel.linePoints = [];
        this._mapModel.distanceMarkers = [];
        this._mapModel.distance = 0;
        this._mapModel.sections = [];
        this.setSectionColorMode(this._model.mapTrackLineName, false);
        this._model.sectionColorMode = false;
        this._model.retraceMode = false;
        this._mapModel.selectedKeypointIndex = null;
        if (this._mapModel.surfaceTypeEnabled) {
            this._mapModel.surfaceTypeEnabled = false;
            this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
        }
        this.removeSurfaceLayers();
        this._mapModel.draggingMarkerId = null;
        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }

        if (this._mapModel.startPointChanged) {
            this._mapModel.startPointChanged(null);
        }

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide();
        }

        if (this.hasModule('ISOCHRONE') && this.clearIsochrone) {
            this.clearIsochrone();
        }

        if (this.hasModule('ELEVATION_COMPUTATION') && this.clearElevationMap) {
            this.clearElevationMap();
        }
    };

    clearSavedData = () => {
        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide();
        }
    };

    simplifyLine = (coordinates, {
        project = false,
        isAdaptiveTolerance = false,
        defaultTolerance = 2.0,
        convertToXY = false,
        highPrecision = true
    } = {}) => {
        let fullPoints = [];
        const polylinesLength = coordinates.length;
        const tolerance = isAdaptiveTolerance ? (polylinesLength > 200 ? Math.round(polylinesLength / 50) : defaultTolerance) : defaultTolerance;

        if (project) {
            for (let coordinateIndex = 0; coordinateIndex < coordinates.length; coordinateIndex++) {
                fullPoints.push(this._model.map.project(coordinates[coordinateIndex]));
            }
        } else if (convertToXY) {
            fullPoints = coordinates.map((point) => ({
                x: point[0],
                y: point[1]
            }));
        } else {
            fullPoints = coordinates;
        }

        const simplifiedPoints = Simplify(fullPoints, tolerance, highPrecision);

        let finalPoints = [];
        if (project) {
            for (var pointIndex = 0; pointIndex < simplifiedPoints.length; pointIndex++) {
                var latLng = this._model.map.unproject(simplifiedPoints[pointIndex]);
                finalPoints.push([latLng.lat, latLng.lng]);
            }
        } else if (convertToXY) {
            finalPoints = simplifiedPoints.map((point) => [point.x, point.y]);
        } else {
            finalPoints = simplifiedPoints;
        }

        return finalPoints;
    };

    staticMapUrl = () => {
        var overlay;
        var size = [600, 500];
        var padding = 100;
        var strokeWidth = 5;
        var lineOpacity = 0.85;
        var mapPosition = 'auto';

        if (Utils.isPresent(this._mapModel.linePoints)) {
            var coordinates = this._mapModel.linePoints.map((linePoint) => [linePoint.latLng.lng, linePoint.latLng.lat]);

            var simplifiedPolyline = Polyline.encode(this.simplifyLine(coordinates, {
                project: true,
                isAdaptiveTolerance: true
            }));

            overlay = 'path-' + strokeWidth + '+' + this._model.mapTrackLineColor.substring(1) + '-' + lineOpacity + '(' + encodeURIComponent(simplifiedPolyline) + ')';
        } else {
            return null;
        }

        return MapRequest.mapboxStaticMapUrl + '/' +
            overlay + '/' +
            mapPosition + '/' +
            size.join('x') +
            '?padding=' + padding +
            '&attribution=false&logo=false' +
            '&access_token=' + window.settings.mapbox_key;
    };

    serializePoints = () => {
        // output format:
        var serializeString = '';

        for (var i = 0; i < this._mapModel.linePoints.length; i++) {
            var currentLinePoint = this._mapModel.linePoints[i];
            var deltaDistance = 0;
            var deltaTime = currentLinePoint.deltaTime;
            var prevLinePoint;
            if (i !== 0) {
                prevLinePoint = this._mapModel.linePoints[i - 1];
                if (prevLinePoint.deltaPause === 0) {
                    deltaDistance = currentLinePoint.deltaDistance || this.distHaversine(this._mapModel.linePoints[i - 1].latLng, currentLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                }

                if (currentLinePoint.deltaTime === 0 && this._mapModel.durationSeconds !== 0) {
                    deltaTime = this._mapModel.durationSeconds / (this._mapModel.linePoints.length - 1);
                }
            }

            var pointInfoType;
            if (i === 0) {
                pointInfoType = 'start';
            } else if (i === this._mapModel.linePoints.length - 1) {
                pointInfoType = 'end';
            } else if (currentLinePoint.deltaPause > 0) {
                pointInfoType = 'resume';
            } else if (this._mapModel.linePoints[i + 1].deltaPause > 0) {
                pointInfoType = 'pause';
            } else if (currentLinePoint.computed) {
                pointInfoType = 'auto';
            } else {
                pointInfoType = 'manual';
            }

            // Serialized structure (from PointInfo):
            // 0 : point type (start, manual, auto, resume, pause, end)
            // 1 : latitude
            // 2 : longitude
            // 3 : elevation
            // 4 : delta distance with previous point
            // 5 : delta time with previous point
            // 6 : delta pause with previous point
            // 7 : datetime / timestamp
            // 8 : speed
            // 9 : heart rate

            if (currentLinePoint.heartRate || currentLinePoint.speed || currentLinePoint.datetime || currentLinePoint.deltaPause) {
                serializeString += [
                    pointInfoType,
                    parseFloat(currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                    parseFloat(currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION)),
                    currentLinePoint.elevation,
                    parseFloat(deltaDistance.toFixed(3)),
                    deltaTime,
                    currentLinePoint.deltaPause,
                    currentLinePoint.datetime,
                    currentLinePoint.speed,
                    currentLinePoint.heartRate
                ].join(',') + ';';
            } else {
                serializeString += [
                    pointInfoType,
                    parseFloat(currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                    parseFloat(currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION)),
                    currentLinePoint.elevation,
                    parseFloat(deltaDistance.toFixed(3))
                ].join(',') + ';';
            }
        }

        return serializeString;
    };

    updatePointsWithElevation = (elevationPoints) => {
        if (elevationPoints.length !== this._mapModel.linePoints.length) {
            return;
        }

        for (var i = 0; i < this._mapModel.linePoints.length; i++) {
            var currentLinePoint = this._mapModel.linePoints[i];

            if (elevationPoints[i] && (elevationPoints[i].elevation || elevationPoints[i].elevation === 0)) {
                if (currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION) === elevationPoints[i].lat.toFixed(MapInterface.COORDINATES_PRECISION) && currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION) === elevationPoints[i].lng.toFixed(MapInterface.COORDINATES_PRECISION)) {
                    currentLinePoint.elevation = elevationPoints[i].elevation;
                }
            }
        }
    };

    toggleSlopePercentage = (enabled, coordinateElevations) => {
        this._mapModel.slopePercentageEnabled = enabled;

        if (enabled) {
            // Disable surface mode if active
            if (this._mapModel.surfaceTypeEnabled) {
                this._mapModel.surfaceTypeEnabled = false;
                this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
            }

            if (coordinateElevations?.length) {
                const slopeGeoJSON = this.buildSlopeGeoJSON(coordinateElevations);
                this.updateSource(this._model.mapTrackLineName, slopeGeoJSON);
                this.setSlopePercentageMode(this._model.mapTrackLineName, true);
            }
        } else {
            this.setSlopePercentageMode(this._model.mapTrackLineName, false);
            this._redrawTripLine();
        }
    };

    applySlopePercentageIfEnabled = (coordinateElevations) => {
        if (!this._mapModel.slopePercentageEnabled || !coordinateElevations?.length) {
            return;
        }

        const slopeGeoJSON = this.buildSlopeGeoJSON(coordinateElevations);
        this.updateSource(this._model.mapTrackLineName, slopeGeoJSON);
        this.setSlopePercentageMode(this._model.mapTrackLineName, true);
    };

    toggleSurfaceType = (enabled) => {
        this._mapModel.surfaceTypeEnabled = enabled;

        if (enabled) {
            // Disable slope mode if active
            if (this._mapModel.slopePercentageEnabled) {
                this._mapModel.slopePercentageEnabled = false;
                this.setSlopePercentageMode(this._model.mapTrackLineName, false);
            }

            this.applySurfaceTypeIfEnabled();
        } else {
            this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
            this._redrawTripLine();
        }
    };

    applySurfaceTypeIfEnabled = () => {
        if (!this._mapModel.surfaceTypeEnabled || !this._mapModel.linePoints?.length) {
            return;
        }

        this._applyStoredSurfacesOnce();

        const surfaceGeoJSON = this.buildSurfaceGeoJSON(this._mapModel.linePoints);
        this.setSurfaceTypeMode(this._model.mapTrackLineName, true, surfaceGeoJSON);
    };

    importTrack = (type, importedTracks, callback) => {
        const importTrackCallback = (newTrackData, isCombined, importCallback) => {
            this.clear();

            if (isCombined) {
                this._mapModel.initialPoints = this._mapModel.initialPoints.concat(newTrackData);
            } else {
                this._mapModel.initialPoints = newTrackData;
            }

            this._model.mapBounds = null;

            this._setupPoints();
            this._redrawKeyPoints();
            this._redrawTripLine();
            this._fitMapBounds();
            if (this.hasModule('TOOLBAR') && this.activateToolbarButtons) {
                this.activateToolbarButtons();
            }

            if (importCallback) {
                importCallback(isCombined, this._mapModel.distance);
            }
        };

        if (type === 'ride' && importedTracks) {
            if (this._mapModel.linePoints.length > 0) {
                this.alertPopup.fire({
                    icon: 'warning',
                    showDenyButton: true,
                    showCancelButton: true,
                    title: I18n.t('js.maps.import.title'),
                    confirmButtonText: I18n.t('js.maps.import.replace'),
                    denyButtonText: I18n.t('js.maps.import.combine'),
                    cancelButtonText: I18n.t('js.helpers.buttons.cancel'),
                })
                    .then((result) => {
                        if (result.isConfirmed) {
                            // Replace current track
                            importTrackCallback(importedTracks, false, callback);
                        } else if (result.isDenied) {
                            // Combine with current track
                            importTrackCallback(importedTracks, true, callback);
                        }
                    });
            } else {
                importTrackCallback(importedTracks, false, callback);
            }
        }
    };

    /* Private Methods
    ******************** */
    _mapInitialSetup = () => {
        if (Utils.isPresent(this._mapModel.initialPoints)) {
            // establish key points to edit with
            this._setupPoints();

            // // draw trip polyline, add to map
            // this._redrawTripLine();

            this._fitMapBounds();
        }
    };

    _setupPoints = () => {
        var i;

        this._mapModel.linePoints = [];

        // Draw polylines points
        if (this._mapModel.initialPoints.length > 0) {
            // var j = -1;
            for (i = 0; i < this._mapModel.initialPoints.length; i++) {
                if (this._mapModel.initialPoints[i].type === 'PoiPoint') {
                    continue;
                    // } else {
                    //     j++;
                }

                var computed = Utils.isPresent(this._mapModel.initialPoints[i].computed) || this._mapModel.initialPoints[i].type === 'auto';
                var deltaDistance = Utils.isEmpty(this._mapModel.initialPoints[i].deltaDistance) ? 0 : this._mapModel.initialPoints[i].deltaDistance;
                var deltaTime = Utils.isEmpty(this._mapModel.initialPoints[i].deltaTime) ? 0 : this._mapModel.initialPoints[i].deltaTime;
                var deltaPause = Utils.isEmpty(this._mapModel.initialPoints[i].deltaPause) ? 0 : this._mapModel.initialPoints[i].deltaPause;
                var datetime = this._mapModel.initialPoints[i].datetime;
                var elevation = this._mapModel.initialPoints[i].elevation;
                var speed = this._mapModel.initialPoints[i].speed;
                var heartRate = this._mapModel.initialPoints[i].heartRate;
                this._mapModel.linePoints.push(new MapInterface.PointInfo(
                    {
                        lat: this._mapModel.initialPoints[i].latitude,
                        lng: this._mapModel.initialPoints[i].longitude
                    },
                    null, // keyPoint
                    {
                        computed: computed, // i !== 0 || i !== this._mapModel.initialPoints.length - 1,
                        deltaDistance: deltaDistance,
                        deltaTime: deltaTime,
                        deltaPause: deltaPause,
                        datetime: datetime,
                        elevation: elevation,
                        speed: speed,
                        heartRate: heartRate
                    }
                ));
            }
        }

        // if we're in edit mode, setup key points with events
        this._redrawKeyPoints();
    };

    _redrawKeyPoints = (options) => {
        options ||= {};

        // clear key points
        this._clearKeyPoints();

        var thisLinePoint;
        var markerName;
        var mapMarkers = {};
        var stepMarkerInterval = this.getZoomDictionary(this._model.map.getZoom()).stepDist;

        // loop through, if the point should be a keypoint, set the keypoint variable and add to map
        var distanceSinceLastKeyPoint = 0;
        var distanceWithEndKeyPoint;
        for (var linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            thisLinePoint = this._mapModel.linePoints[linePointIndex];

            if (linePointIndex === 0) {
                if (options.refresh) {
                    continue;
                }

                // set start point options
                markerName = 'start-marker';
            } else if (linePointIndex === this._mapModel.linePoints.length - 1) {
                if (options.refresh) {
                    continue;
                }

                // set finish point options
                markerName = 'finish-marker';
            } else if (this._mapModel.linePoints[linePointIndex + 1].deltaPause > 0) {
                // set pause point options
                markerName = 'pause-marker';
            } else if (thisLinePoint.deltaPause > 0) {
                // set resume point options
                markerName = 'resume-marker';
            } else if (thisLinePoint.computed) {
                // if point is computed, display points accordingly to the current zoom
                var prevLineComputedPoint = this._mapModel.linePoints[linePointIndex - 1];
                distanceSinceLastKeyPoint += this.distHaversine(prevLineComputedPoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                distanceWithEndKeyPoint = this.distHaversine(thisLinePoint.latLng, this._mapModel.linePoints[this._mapModel.linePoints.length - 1].latLng, MapInterface.COORDINATES_PRECISION);

                // if more than X meters have passed since last key point, make a normal key point.
                // otherwise, simply kick the distance since last key point variable
                if (stepMarkerInterval && distanceSinceLastKeyPoint > stepMarkerInterval && distanceWithEndKeyPoint > 100) {
                    markerName = 'step-marker';
                    distanceSinceLastKeyPoint = 0;
                } else {
                    markerName = null;
                }
            } else {
                // normal point need to be present when dragging the siblings points
                markerName = 'step-marker';
            }

            if (markerName && thisLinePoint.latLng) {
                thisLinePoint.keyPoint = {
                    type: 'Feature',
                    properties: {
                        markerId: markerName,
                        linePointIndex: linePointIndex,
                        draggable: this._mapModel.editMode
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [thisLinePoint.latLng.lng, thisLinePoint.latLng.lat]
                    }
                };
                mapMarkers[markerName] = (mapMarkers[markerName] || []).concat(thisLinePoint.keyPoint);
            }
        }

        Object.keys(mapMarkers)
            .forEach((markerType) => {
                this.updateSource(markerType, {
                    type: 'FeatureCollection',
                    features: mapMarkers[markerType]
                });
            });

        if (this._mapModel.linePoints.length > 1 && !options.refresh) {
            this._redrawTripLine();
        } else if (this._mapModel.linePoints.length <= 1) {
            // No segment left: make sure a stale off-road overlay doesn't linger.
            this.resetMapSource(this._model.mapTrackLineName + '-offroad');
        }
    };

    _redrawTripLine = () => {
        if (!this._model.map) {
            return;
        }

        // Restore persisted surfaces onto the freshly loaded points (once), before any redraw.
        this._applyStoredSurfacesOnce();

        let linePointIndex;

        // During an active marker drag this runs on every mousemove. Skip the distance-marker
        // DOM work (removing then re-creating maplibre Markers) to avoid per-frame churn: the
        // existing markers stay in place and are fully rebuilt once when the drag ends.
        const isDragging = !!this._mapModel.draggingMarkerId;

        // remove markers
        if (!isDragging) {
            this._removeDistanceMarkers();
        }

        // clear pointsByDistanceTraveled object (used by highCharts)
        this._model.pointsByDistanceTraveled = {};

        // set initial 'total Distance' variable
        let totalDistance = 0;

        // Reset to default colors while redrawing (slope/surface colors will be re-applied after)
        if (this._mapModel.slopePercentageEnabled) {
            this.setSlopePercentageMode(this._model.mapTrackLineName, false);
        }
        if (this._mapModel.surfaceTypeEnabled) {
            this.setSurfaceTypeMode(this._model.mapTrackLineName, false);
        }

        // remove all activity line polylines from the map
        this.resetMapSource(this._model.mapTrackLineName);

        // // clear both arrays for re-use
        // this._mapModel.activityPolylines = [];

        // distance variables for lines
        let lastLineStartPointIndex = 0;
        let lastLinePointTotalDistance = 0;

        // what distance marker we are currently on
        let markerCount = 0;

        // Only trust the cached per-point deltaDistance (authoritative source distance from an
        // imported/shared/saved ride) while the track is pristine. As soon as the user edits,
        // points get spliced/deleted and a point's previous neighbour may change, leaving its
        // cached delta stale; from then on we recompute every leg geometrically.
        const trustCachedDeltas = this._mapModel.snapShotStack.length === 0;

        const mapZoom = this._model.map.getZoom();
        for (linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            let thisLegDist = 0;

            // if we have reached a pause point OR the last point (finish) draw this segment and process
            if (linePointIndex === this._mapModel.linePoints.length - 1 /* || this._mapModel.linePoints[linePointIndex + 1].deltaPause > 0 */) {
                // draw this line segment
                const nextLine = [];
                for (let j = lastLineStartPointIndex; j <= linePointIndex; j++) {
                    const thisLinePointLatLng = this._mapModel.linePoints[j].latLng;
                    let prevLinePointLatLng;

                    nextLine.push(this._mapModel.linePoints[j].latLng);

                    if (j !== lastLineStartPointIndex) {
                        prevLinePointLatLng = this._mapModel.linePoints[j - 1].latLng;

                        if (trustCachedDeltas && this._mapModel.linePoints[j].deltaDistance) {
                            thisLegDist = this._mapModel.linePoints[j].deltaDistance;
                            totalDistance += thisLegDist;
                        } else {
                            thisLegDist = this.distHaversine(prevLinePointLatLng, thisLinePointLatLng, MapInterface.COORDINATES_PRECISION);
                            this._mapModel.linePoints[j].deltaDistance = thisLegDist;
                            totalDistance += thisLegDist;
                        }
                    }

                    // Check if max distance is reached
                    if (totalDistance >= this._model.mapMaxDistance) {
                        this.undo();

                        this.leaveFullscreen();

                        if (this._model.showProductModal) {
                            this._model.showProductModal('map_max_distance');
                        }

                        return;
                    }

                    // }

                    // handle the total distance stamp for Highcharts point mapping
                    this._model.pointsByDistanceTraveled[Math.floor(totalDistance)] = thisLinePointLatLng;

                    // Are we 1 distance unit (or more) greater? try to draw a distance marker
                    const curDist = totalDistance / 1000 / (this._model.distanceUnits === 'mi' ? 1.609344 : 1);
                    if (!isDragging && curDist >= markerCount + 1) {
                        const markersToAdd = Math.floor(curDist) - markerCount;
                        for (let k = 0; k < markersToAdd; k++) {
                            // kick marker number
                            markerCount++;

                            // if zoomed in far enough, show all individual mile markers
                            // if not zoomed in past 13, show every 5 if at 12
                            // if not zoomed in past 12, show every 10
                            if ((mapZoom >= 13) || ((mapZoom >= 12) && (markerCount % 5 === 0)) || ((mapZoom >= 11) && (markerCount % 10 === 0))) {
                                // find lat lng
                                const distanceToDistMarker = markerCount - lastLinePointTotalDistance;
                                let percentageDist;
                                if (this._model.distanceUnits === 'mi') {
                                    percentageDist = distanceToDistMarker / (thisLegDist / 1000 / 1.609344);
                                } else {
                                    percentageDist = distanceToDistMarker / (thisLegDist / 1000);
                                }

                                // lat lng of distance marker to place
                                const distancePointLat = prevLinePointLatLng.lat + ((thisLinePointLatLng.lat - prevLinePointLatLng.lat) * percentageDist);
                                const distancePointLng = prevLinePointLatLng.lng + ((thisLinePointLatLng.lng - prevLinePointLatLng.lng) * percentageDist);

                                var textSizeChange = '';
                                if (markerCount > 99) {
                                    textSizeChange = ' style="font-size: 9px;"';
                                }

                                // Set up icon + marker
                                const distanceMarker = new MapInterface.MarkerPoint(
                                    this.buildMarker('distance-unit-marker', {
                                        lat: distancePointLat,
                                        lng: distancePointLng
                                    }, {
                                        clickable: false,
                                        draggable: false,
                                        markerContent: '<div class="number"' + textSizeChange + '>' + markerCount + '</div><div class="dist-unit">' + this._model.distanceUnits + '</div></div>',
                                        markerImage: markerDistanceIcon
                                    }),
                                    markerCount
                                );

                                // push to distance marker array
                                this._mapModel.distanceMarkers.push(distanceMarker);

                                // draw this distance marker on map
                                distanceMarker.marker?.addTo(this._model.map);
                            }
                        }
                    }

                    // update last line point distance stamp
                    lastLinePointTotalDistance = curDist;
                }

                // draw lines on map and thumbnails in the set color
                const thisLine = {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: nextLine.map((lngLat) => [lngLat.lng, lngLat.lat])
                    }
                };

                this.updateSource(this._model.mapTrackLineName, thisLine);

                // this._mapModel.activityPolylines.push(thisLine.geometry.coordinates);

                // kick last line start point index (start at the next point, the resume)
                lastLineStartPointIndex = linePointIndex + 1;
            }
        }

        // distance to km
        this._mapModel.distance = Utils.flooredNum(totalDistance, MapInterface.COORDINATES_PRECISION) / 1000;

        // Refresh the off-road overlay (freehand segments not matched to a road)
        this.updateSource(this._model.mapTrackLineName + '-offroad', this._buildOffRoadGeoJSON());

        // Re-apply surface color if enabled (surface data is already on linePoints)
        this.applySurfaceTypeIfEnabled();

        // Re-apply per-section colours if any (slope/surface views take precedence)
        this.applySectionColorsIfEnabled();

        // run distance updated function stack
        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }
    };

    _removeDistanceMarkers = () => {
        // Start by removing the ghost points already there
        for (var i = 0; i < this._mapModel.distanceMarkers.length; i++) {
            this._mapModel.distanceMarkers[i].marker?.remove();
        }

        this._mapModel.distanceMarkers = [];
    };

    _drawDirectionArrows = () => {
        this._removeDirectionArrows();

        // how often SHOULD we be placing direction arrows?
        var directionArrowInterval = this.getZoomDictionary(this._model.map.getZoom()).directionArrow;
        if (!directionArrowInterval) {
            return;
        }

        // draw a fake point in between every key point to allow user to insert a new point here.
        if (this._mapModel.linePoints.length > 1) {
            // variable for distance since last direction arrow
            var distanceSinceLastDirectionArrow = 0;

            // after placing a direction arrow, how much distance is left over (so we can place correctly after the next invisible point)
            var carryoverDistance = 0;
            var lastKeyPointIndex = 0;
            for (var linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
                var thisLinePoint;
                var prevLinePoint;
                thisLinePoint = this._mapModel.linePoints[linePointIndex];

                // variable for point-to-point distance
                var distanceBetween = 0;
                if (linePointIndex > 0) {
                    prevLinePoint = this._mapModel.linePoints[linePointIndex - 1];
                    distanceBetween = this.distHaversine(prevLinePoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                    distanceSinceLastDirectionArrow += distanceBetween;
                }

                // if we've traveled more than the direction arrow interval, find out how many to place
                var directionArrowsToPlace = 0;
                if (distanceSinceLastDirectionArrow > directionArrowInterval) {
                    directionArrowsToPlace = Math.floor(distanceSinceLastDirectionArrow / directionArrowInterval);
                }

                // start with carryover distance
                // move from point A to point B, placing direction arrows at interval specified (remove carryover from last loop iteration)
                // when done, set carryover distance and continue
                if (directionArrowsToPlace > 0) {
                    for (var directionArrowIndex = 1; directionArrowIndex <= directionArrowsToPlace; directionArrowIndex++) {
                        var directionArrowLat;
                        var directionArrowLng;
                        var distanceToNextDirectionArrow = (directionArrowInterval * directionArrowIndex) - carryoverDistance;
                        var percentageBetweenPoints = distanceToNextDirectionArrow / distanceBetween;

                        // we now know at what percentage distance the direction arrow should be placed. Use to put it on the line
                        directionArrowLat = prevLinePoint.latLng.lat + ((thisLinePoint.latLng.lat - prevLinePoint.latLng.lat) * percentageBetweenPoints);
                        directionArrowLng = prevLinePoint.latLng.lng + ((thisLinePoint.latLng.lng - prevLinePoint.latLng.lng) * percentageBetweenPoints);

                        var diffLat = thisLinePoint.latLng.lat - prevLinePoint.latLng.lat;
                        var diffLng = thisLinePoint.latLng.lng - prevLinePoint.latLng.lng;
                        // var center = [thisLinePoint.latLng.lat + diffLat / 2, thisLinePoint.latLng.lng + diffLng / 2];
                        var angle = (360 - ((Math.atan2(diffLat, diffLng) * 57.295779513082) % 360));

                        // create direction arrow object
                        var newDirectionArrow = new MapInterface.DirectionArrow(
                            {
                                type: 'Feature',
                                properties: {
                                    angle: angle,
                                    linePointIndex: linePointIndex,
                                    directionArrowIndex: directionArrowIndex
                                },
                                geometry: {
                                    type: 'Point',
                                    coordinates: [directionArrowLng, directionArrowLat]
                                }
                            },
                            lastKeyPointIndex
                        );

                        // add to array for reference
                        this._mapModel.directionArrows.push(newDirectionArrow);

                        if (directionArrowIndex === directionArrowsToPlace) {
                            // last need
                            carryoverDistance = distanceBetween - (percentageBetweenPoints * distanceBetween);
                            distanceSinceLastDirectionArrow = carryoverDistance;
                        }
                    }
                } else {
                    carryoverDistance += distanceBetween;
                }

                if (thisLinePoint && thisLinePoint.keyPoint !== null) {
                    lastKeyPointIndex = linePointIndex;
                }
            }

            this.updateSource('arrow-marker', {
                type: 'FeatureCollection',
                features: this._mapModel.directionArrows.map((directionArrow) => directionArrow.keyPoint)
            });
        }
    };

    _removeDirectionArrows = () => {
        if (!this._mapModel.directionArrows.length) {
            return;
        }

        this.resetMapSource('arrow-marker');

        this._mapModel.directionArrows = [];
    };

    _manageEventAction = (callback, event) => {
        event.preventDefault();
        event.originalEvent.stopPropagation();

        callback(this._convertPopupCoordinates(event), event.features[0].properties);
    };

    _onSourceEvent = (eventName, sourceIds, callback) => {
        var eventListener = this._manageEventAction.bind(this, callback);

        if (Array.isArray(sourceIds)) {
            sourceIds.forEach((sourceId) => {
                this._model.map.on(eventName, sourceId, eventListener);
            });
        } else {
            this._model.map.on(eventName, sourceIds, eventListener);
        }
    };

    _onSourceLongTouch = (sourceIds, callback) => {
        var longTouchDuration = 500;

        var touchTimeout = null;
        var clearTouchTimeout = () => {
            this._model.isLongTouching = false;
            clearTimeout(touchTimeout);
        };

        if (Array.isArray(sourceIds)) {
            sourceIds.forEach((sourceId) => {
                this._model.map.on('touchstart', sourceId, (event) => {
                    if (event.originalEvent.touches.length > 1) {
                        return;
                    }

                    this._model.isLongTouching = true;

                    var features = event.features;
                    touchTimeout = setTimeout(function (touchEvent) {
                        touchEvent.features = features;
                        this._manageEventAction(callback, touchEvent);
                    }.bind(this, event), longTouchDuration);
                });

                this._model.map.on('touchend', sourceId, clearTouchTimeout);
                this._model.map.on('touchcancel', sourceId, clearTouchTimeout);
                this._model.map.on('touchmove', sourceId, clearTouchTimeout);
            });
        } else {
            this._model.map.on('touchstart', sourceIds, (event) => {
                if (event.originalEvent.touches.length > 1) {
                    return;
                }

                this._model.isLongTouching = true;

                var features = event.features;
                touchTimeout = setTimeout(function (touchEvent) {
                    touchEvent.features = features;
                    this._manageEventAction(callback, touchEvent);
                }.bind(this, event), longTouchDuration);
            });

            this._model.map.on('touchend', sourceIds, clearTouchTimeout);
            this._model.map.on('touchcancel', sourceIds, clearTouchTimeout);
            this._model.map.on('touchmove', sourceIds, clearTouchTimeout);
        }
    };

    _onDragSource = (sourceId, onDragStart, onDragging, onDragEnd, markerDragProperties) => {
        const canvas = this._model.map.getCanvasContainer();
        let markerProperties;
        markerDragProperties ||= {};

        const onDragMove = (event) => {
            var coordinates = event.lngLat;

            canvas.style.cursor = 'grabbing';

            onDragging(sourceId, coordinates, markerProperties);
        };

        const onDragUp = (event) => {
            var coordinates = event.lngLat;

            canvas.style.cursor = '';

            onDragEnd(sourceId, coordinates, markerProperties);

            // Unbind mouse/touch events
            this._model.map.off('mousemove', onDragMove);
            this._model.map.off('touchmove', onDragMove);
        };

        // When the cursor enters a feature in the point layer, prepare for dragging.
        this._model.map.on('mouseenter', sourceId, () => {
            if (!this._model.map || !this._model.map.isStyleLoaded() || !this._model.map.getLayer(sourceId)) {
                return;
            }

            this._model.map.setPaintProperty(sourceId, 'icon-opacity', typeof markerDragProperties.hoverOpacity === 'undefined' ? 1 : markerDragProperties.hoverOpacity);

            canvas.style.cursor = 'move';
        });

        this._model.map.on('mouseleave', sourceId, () => {
            if (!this._model.map || !this._model.map.isStyleLoaded() || !this._model.map.getLayer(sourceId)) {
                return;
            }

            this._model.map.setPaintProperty(sourceId, 'icon-opacity', typeof markerDragProperties.defaultOpacity === 'undefined' ? 1 : markerDragProperties.defaultOpacity);

            canvas.style.cursor = '';
        });

        this._model.map.on('mousedown', sourceId, (event) => {
            // Prevent move if not left click, or while a freehand stroke owns the pointer
            if (event.originalEvent.button !== 0 || this._model.isLongTouching || this._model.poiModeEnabled || this._model.waypointModeEnabled || this._model.freehandDraw) {
                return;
            }

            // Prevent the default map drag behavior
            event.preventDefault();
            event.originalEvent.stopPropagation();

            canvas.style.cursor = 'grab';

            markerProperties = event.features[0].properties;
            onDragStart(sourceId, event.lngLat, markerProperties);
            this._model.map.on('mousemove', onDragMove);
            this._model.map.once('mouseup', onDragUp);
        });

        this._model.map.on('touchstart', sourceId, (event) => {
            if (event.points.length !== 1 || this._model.isLongTouching || this._model.poiModeEnabled || this._model.waypointModeEnabled || this._model.freehandDraw) {
                return;
            }

            // Prevent the default map drag behavior.
            event.preventDefault();
            event.originalEvent.stopPropagation();

            markerProperties = event.features[0].properties;
            onDragStart(sourceId, event.lngLat, markerProperties);
            this._model.map.on('touchmove', onDragMove);
            this._model.map.once('touchend', onDragUp);
        });
    };

    _addKeyPointDebounce = Utils.debounce((event) => {
        if (this._model.isDoubleClick) {
            this._model.isDoubleClick = false;
            return;
        }

        // Avoid double click
        this._addKeyPointAtClickLoc(event.lngLat);
    }, 200);

    _addKeyPointEventHandlers = () => {
        this._onSourceEvent('click', ['start-marker', 'step-marker', 'finish-marker'], (coordinates, markerProperties) => {
            this._addLinePoint(coordinates, markerProperties);
        });

        this._onSourceEvent('contextmenu', ['start-marker', 'step-marker', 'finish-marker'], (coordinates, markerProperties) => {
            this._removeLinePoint(coordinates, markerProperties);
        });

        this._onSourceLongTouch(['start-marker', 'step-marker', 'finish-marker'], (coordinates, markerProperties) => {
            this._removeLinePoint(coordinates, markerProperties);
        });

        ['start-marker', 'step-marker', 'finish-marker'].forEach((markerId) => {
            this._onDragSource(
                markerId,
                this._onMarkerDragStart.bind(this),
                this._onMarkerDragging.bind(this),
                this._onMarkerDragEnd.bind(this)
            );
        });

        this._addLineDragToReroute();

        this._addNudgeHandlers();

        // this.onDragSource(
        //     'ghost-marker',
        //     this._onGhostPointDragStart.bind(this),
        //     this._onGhostPointDragging.bind(this),
        //     this._onGhostPointDragEnd.bind(this),
        //     {
        //         defaultOpacity: 0,
        //         hoverOpacity: 0.7
        //     }
        // );
    };

    // Index at which to insert a point so it lands on the segment closest to `lngLat`
    // (between points[index-1] and points[index]).
    _nearestSegmentInsertIndex = (lngLat) => {
        const points = this._mapModel.linePoints;
        if (points.length < 2) {
            return null;
        }

        let bestIndex = null;
        let bestDistance = Infinity;
        for (let i = 1; i < points.length; i++) {
            const distance = this._distanceToSegmentMeters(lngLat, points[i - 1].latLng, points[i].latLng);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestIndex = i;
            }
        }

        return bestIndex;
    };

    // Project a point onto the drawn curve. Returns the nearest point that lies ON the polyline
    // (snapped), the segment it belongs to (end index), the clamped position `t` along that segment,
    // and the distance in meters. Used to keep waypoints strictly on the track.
    projectOntoPolyline = (lngLat) => {
        const points = this._mapModel.linePoints;
        if (!points || points.length < 2) {
            return null;
        }

        const metersPerDegLat = (MapInterface.EARTH_RADIUS * 1000 * Math.PI) / 180;
        const metersPerDegLng = metersPerDegLat * Math.cos(lngLat.lat * MapInterface.DEG_TO_RAD);

        let best = null;
        let cumulative = 0; // distance from the route start to the current segment start (m)
        for (let i = 1; i < points.length; i++) {
            const segStart = points[i - 1].latLng;
            const segEnd = points[i].latLng;
            const segLength = this.distHaversine(segStart, segEnd, MapInterface.COORDINATES_PRECISION);

            const ax = (segStart.lng - lngLat.lng) * metersPerDegLng;
            const ay = (segStart.lat - lngLat.lat) * metersPerDegLat;
            const bx = (segEnd.lng - lngLat.lng) * metersPerDegLng;
            const by = (segEnd.lat - lngLat.lat) * metersPerDegLat;

            const dx = bx - ax;
            const dy = by - ay;
            const lengthSq = dx * dx + dy * dy;

            let t = lengthSq === 0 ? 0 : -(ax * dx + ay * dy) / lengthSq;
            t = Math.max(0, Math.min(1, t));

            const distanceMeters = Math.hypot(ax + t * dx, ay + t * dy);

            if (!best || distanceMeters < best.distanceMeters) {
                best = {
                    lat: segStart.lat + t * (segEnd.lat - segStart.lat),
                    lng: segStart.lng + t * (segEnd.lng - segStart.lng),
                    segmentIndex: i,
                    t: t,
                    distanceMeters: distanceMeters,
                    // Along-track distance to the projected point (used to order POIs along the route).
                    distanceAlong: cumulative + t * segLength
                };
            }

            cumulative += segLength;
        }

        return best;
    };

    // Grab the track line itself to insert a waypoint and reroute (drag-to-reroute). A click without
    // moving inserts a point on the line at that spot. Reuses the marker-drag flow: a fresh manual
    // keypoint is inserted, then driven through _onMarkerDragStart/Dragging/End exactly like moving
    // an existing midpoint (neighbour computed points are removed and both legs re-routed).
    _addLineDragToReroute = () => {
        const lineLayers = [0, 1, 2].map((i) => this._model.mapTrackLineName + '-' + i);
        const canvas = this._model.map.getCanvasContainer();

        let markerProperties = null;

        const canReroute = () => this._mapModel.editMode
            && !this._model.poiModeEnabled
            && !this._model.waypointModeEnabled
            && !this._model.sectionColorMode
            && !this._model.retraceMode
            && !this._model.freehandDraw
            && !this._model.isComputingRoad
            && !this._mapModel.draggingMarkerId
            && this._mapModel.linePoints.length >= 2;

        const onMove = (event) => {
            if (!markerProperties) {
                return;
            }
            canvas.style.cursor = 'grabbing';
            this._onMarkerDragging('step-marker', event.lngLat, markerProperties);
        };

        const onUp = (event) => {
            this._model.map.off('mousemove', onMove);
            this._model.map.off('touchmove', onMove);
            canvas.style.cursor = '';

            if (!markerProperties) {
                return;
            }

            this._onMarkerDragEnd('step-marker', event.lngLat, markerProperties);
            markerProperties = null;

            // Swallow the click that a tap (mousedown+up without a drag) would otherwise turn into
            // a new end point.
            this._mapModel.markerEventClicked = true;

            if (this.hasModule('RIDE_STORE') && this.storeRide) {
                this.storeRide(this._mapModel.linePoints);
            }
        };

        const onDown = (event, isTouch) => {
            if (!canReroute() || (!isTouch && event.originalEvent.button !== 0)) {
                return;
            }

            // Skip when the press started on a POI/waypoint marker (a DOM overlay): the user is dragging
            // that marker, not rerouting the line underneath it. Route keypoints are GL layers, so only
            // POI markers ever match — this stops a waypoint drag from also dragging the curve.
            const pressTarget = event.originalEvent?.target;
            if (pressTarget && pressTarget.closest && pressTarget.closest('.maplibregl-marker')) {
                return;
            }

            const insertAt = this._nearestSegmentInsertIndex(event.lngLat);
            if (insertAt === null) {
                return;
            }

            event.preventDefault();
            event.originalEvent.stopPropagation();

            // Insert a manual keypoint at the grabbed spot, then drive the standard waypoint-drag
            // flow for it (handles neighbour cleanup + rerouting on release).
            this._mapModel.linePoints.splice(insertAt, 0, new MapInterface.PointInfo(event.lngLat, null, {computed: false}));

            markerProperties = {linePointIndex: insertAt};
            this._onMarkerDragStart('step-marker', event.lngLat, markerProperties);

            if (isTouch) {
                this._model.map.on('touchmove', onMove);
                this._model.map.once('touchend', onUp);
            } else {
                this._model.map.on('mousemove', onMove);
                this._model.map.once('mouseup', onUp);
            }
        };

        lineLayers.forEach((layerId) => {
            this._model.map.on('mousedown', layerId, (event) => onDown(event, false));
            this._model.map.on('touchstart', layerId, (event) => {
                if (event.points && event.points.length !== 1) {
                    return;
                }
                onDown(event, true);
            });

            this._model.map.on('mouseenter', layerId, () => {
                if (canReroute()) {
                    canvas.style.cursor = 'grab';
                }
            });
            this._model.map.on('mouseleave', layerId, () => {
                if (!markerProperties) {
                    canvas.style.cursor = '';
                }
            });
        });
    };

    // Select a keypoint for keyboard fine-adjust (nudge) and focus the canvas so arrows reach us.
    _selectKeypointForNudge = (index) => {
        this._mapModel.selectedKeypointIndex = index;

        const container = this._model.map.getCanvasContainer();
        if (container && typeof container.focus === 'function') {
            container.focus();
        }

        if (!this._mapModel.nudgeHintShown) {
            this._mapModel.nudgeHintShown = true;
            Notification.info(I18n.t('js.maps.helpers.nudge'));
        }
    };

    _addNudgeHandlers = () => {
        const container = this._model.map.getCanvasContainer();
        if (!container) {
            return;
        }

        // Make the container programmatically focusable so arrow keys reach the handler after a
        // keypoint is selected (the container has no tabindex by default → .focus() would be a no-op).
        if (!container.hasAttribute('tabindex')) {
            container.setAttribute('tabindex', '-1');
            container.style.outline = 'none';
        }

        container.addEventListener('keydown', (event) => {
            if (this._mapModel.selectedKeypointIndex === null) {
                return;
            }

            if (event.key === 'Escape') {
                this._mapModel.selectedKeypointIndex = null;
                return;
            }

            // ~3 m per press, ~1 m with Shift for finer control
            const step = event.shiftKey ? 0.00001 : 0.00003;
            let deltaLat = 0;
            let deltaLng = 0;
            switch (event.key) {
                case 'ArrowUp': deltaLat = step; break;
                case 'ArrowDown': deltaLat = -step; break;
                case 'ArrowLeft': deltaLng = -step; break;
                case 'ArrowRight': deltaLng = step; break;
                default: return;
            }

            event.preventDefault();
            this._nudgeSelectedKeypoint(deltaLat, deltaLng);
        });
    };

    // Fine-move the selected keypoint. It stays a manual point with straight neighbour segments — no
    // routing call, so it is quota-free and instant; the user can drag it afterwards to re-snap.
    _nudgeSelectedKeypoint = (deltaLat, deltaLng) => {
        const index = this._mapModel.selectedKeypointIndex;
        const point = this._mapModel.linePoints[index];
        if (!point) {
            this._mapModel.selectedKeypointIndex = null;
            return;
        }

        // One undo step per nudge burst. _pushUndoSnapShot clears the selection, so restore it
        // afterwards to keep nudging the same point across key presses.
        if (!this._model.isNudging) {
            this._pushUndoSnapShot();
            this._model.isNudging = true;
        }
        this._mapModel.selectedKeypointIndex = index;

        point.latLng = {
            lat: point.latLng.lat + deltaLat,
            lng: point.latLng.lng + deltaLng
        };
        point.computed = false;

        // Coalesce the O(n) redraw to one per animation frame during key auto-repeat, and persist
        // only once the burst ends — instead of redrawing + writing localStorage on every keypress.
        if (!this._nudgeRedrawScheduled) {
            this._nudgeRedrawScheduled = true;
            requestAnimationFrame(() => {
                this._nudgeRedrawScheduled = false;
                this._redrawKeyPoints();
                this._mapEditedFuncs();
            });
        }

        clearTimeout(this._nudgeBurstTimeout);
        this._nudgeBurstTimeout = setTimeout(() => {
            this._model.isNudging = false;
            if (this.hasModule('RIDE_STORE') && this.storeRide) {
                this.storeRide(this._mapModel.linePoints);
            }
        }, 800);
    };

    _addLinePoint = (coordinates, markerProperties) => {
        // In POI/waypoint mode, track markers are inert
        if (this._model.poiModeEnabled || this._model.waypointModeEnabled) {
            return;
        }

        if (typeof markerProperties.linePointIndex === 'undefined') {
            return;
        }

        // Edit mode: clicking a keypoint selects it for keyboard fine-adjust (nudge)
        if (this._mapModel.editMode) {
            this._selectKeypointForNudge(markerProperties.linePointIndex);
            return;
        }

        // Merge start and end point if click on first point
        if (markerProperties.linePointIndex !== 0) {
            return;
        }

        if (this._mapModel.editMode) {
            return;
        }

        this._addKeyPointAtClickLoc(coordinates);
    };

    _removeLinePoint = (coordinates, markerProperties) => {
        // In POI/waypoint mode, track markers are inert
        if (this._model.poiModeEnabled || this._model.waypointModeEnabled) {
            return;
        }

        this._pushUndoSnapShot();

        var linePointIndex = markerProperties.linePointIndex;
        markerProperties.prevKeyPointIndex = this._getPrevKeyPointIndex(linePointIndex, true);
        markerProperties.nextKeyPointIndex = this._getNextKeyPointIndex(linePointIndex, true);
        var deleteCurrentPointAsWell = (linePointIndex === markerProperties.nextKeyPointIndex || linePointIndex === markerProperties.prevKeyPointIndex);

        this._removeLinePointsBetween(markerProperties.prevKeyPointIndex, markerProperties.nextKeyPointIndex);
        var isFirstPoint = markerProperties.markerId === 'start-marker' || (linePointIndex === 0);
        var isLastPoint = markerProperties.markerId === 'finish-marker' || (this._mapModel.linePoints.length - 1 === linePointIndex);

        linePointIndex -= (linePointIndex - markerProperties.prevKeyPointIndex);

        if (deleteCurrentPointAsWell) {
            if (isFirstPoint) {
                this._mapModel.linePoints.splice(0, 1);
            } else if (isLastPoint) {
                this._mapModel.linePoints.splice(this._mapModel.linePoints.length - 1, 1);
            } else {
                this._mapModel.linePoints.splice(linePointIndex, 1);
            }
        }

        markerProperties.linePointIndex = linePointIndex;

        this._redrawKeyPoints();

        if (!this._mapModel.linePoints.length) {
            this.resetMapSource('start-marker');
        } else if (this._mapModel.linePoints.length === 1) {
            this._redrawTripLine();
        }

        this._mapEditedFuncs();

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    _removeLinePointsBetween = (startIndex, endIndex) => {
        if (startIndex === null || startIndex === undefined || typeof startIndex === 'undefined' ||
            endIndex === null || endIndex === undefined || typeof endIndex === 'undefined') {
            return [];
        }
        var totalToDelete = endIndex - startIndex - 1;
        var deletedPoints = this._mapModel.linePoints.splice(startIndex + 1, totalToDelete);
        return deletedPoints;
    };

    _onMarkerDragStart = (markerId, coordinates, markerProperties) => {
        // Prevent double dragging
        if (this._mapModel.draggingMarkerId && this._mapModel.draggingMarkerId !== markerId) {
            return;
        }
        this._mapModel.draggingMarkerId = markerId;
        this._model.previousRoadComputedPoints = 0;

        this._pushUndoSnapShot();

        // remove direction arrows
        this._removeDirectionArrows();

        // event handlers for drag start, drag end, drag
        var linePointIndex = markerProperties.linePointIndex;
        // get next keypoint line point index, previous keypoint line point index
        markerProperties.prevKeyPointIndex = this._getPrevKeyPointIndex(linePointIndex);
        markerProperties.nextKeyPointIndex = this._getNextKeyPointIndex(linePointIndex);

        if (markerId !== 'start-marker') {
            var deletedPointsBefore;
            var deletedPointsAfter;

            if (markerProperties.prevKeyPointIndex !== null) {
                // delete points before
                deletedPointsBefore = this._removeLinePointsBetween(markerProperties.prevKeyPointIndex, linePointIndex);
                // points have been removed, adjust index references
                linePointIndex -= deletedPointsBefore.length;
                markerProperties.nextKeyPointIndex -= deletedPointsBefore.length;
            }

            if (markerProperties.nextKeyPointIndex !== null) {
                // delete points after
                deletedPointsAfter = this._removeLinePointsBetween(linePointIndex, markerProperties.nextKeyPointIndex);
                // points have been removed, adjust index references
                markerProperties.nextKeyPointIndex -= deletedPointsAfter.length;
            }
        }

        markerProperties.linePointIndex = linePointIndex;

        this.resetMapSource('step-marker');
    };

    _onMarkerDragging = (markerId, coordinates, markerProperties) => {
        // Prevent double dragging
        if (this._mapModel.draggingMarkerId && this._mapModel.draggingMarkerId !== markerId) {
            return;
        }

        var linePointIndex = markerProperties.linePointIndex;
        var thisLinePoint = this._mapModel.linePoints[linePointIndex];
        if (!thisLinePoint) {
            return;
        }

        thisLinePoint.computed = false;

        thisLinePoint.latLng = coordinates;

        if (markerId !== 'step-marker' && this._model.snapToRoads && this.isMapPlannerRouting(this._model.routingService)) {
            var startingPoint;
            var endingPoint;

            if (markerId === 'start-marker') {
                startingPoint = thisLinePoint;
                if (this._mapModel.linePoints.length > 1) {
                    endingPoint = this._mapModel.linePoints[linePointIndex + 1 + this._model.previousRoadComputedPoints];
                }
            } else if (markerId === 'finish-marker') {
                startingPoint = this._mapModel.linePoints[linePointIndex - 1];
                endingPoint = thisLinePoint;
            }

            if (startingPoint && endingPoint) {
                this.getSnapToRoadsRoute(
                    startingPoint.latLng,
                    endingPoint.latLng,
                    null,
                    (pointArray) => {
                        if (typeof pointArray !== 'undefined') {
                            var points = [];
                            for (var routePointIndex = 0; routePointIndex < pointArray.length; routePointIndex++) {
                                points.push(new MapInterface.PointInfo(pointArray[routePointIndex], null, {
                                    computed: true,
                                    surface: pointArray[routePointIndex].surface
                                }));
                            }

                            if (markerId === 'start-marker') {
                                Array.prototype.splice.apply(this._mapModel.linePoints, [0, this._model.previousRoadComputedPoints || 1].concat(points));
                            } else {
                                Array.prototype.splice.apply(this._mapModel.linePoints, [linePointIndex, this._model.previousRoadComputedPoints + 1].concat(points));
                            }

                            this._model.previousRoadComputedPoints = points.length;
                        }

                        this._redrawKeyPoints();
                    },
                    {
                        limitDistance: true,
                        distance: this._mapModel.distance
                    }
                );
            } else {
                this._redrawKeyPoints();
            }
        } else {
            this._redrawTripLine();

            // Update collection with only one point when dragging, display all points when dragging end
            this.updateSource(markerId, {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: markerProperties,
                        geometry: {
                            type: 'Point',
                            coordinates: [thisLinePoint.latLng.lng, thisLinePoint.latLng.lat]
                        }
                    }
                ]
            });
        }
    };

    _onMarkerDragEnd = (markerId, coordinates, markerProperties) => {
        // Prevent double dragging
        if (this._mapModel.draggingMarkerId && this._mapModel.draggingMarkerId !== markerId) {
            return;
        }
        this._mapModel.draggingMarkerId = null;
        this._model.previousRoadComputedPoints = 0;

        var linePointIndex = markerProperties.linePointIndex;
        var thisLinePoint = this._mapModel.linePoints[linePointIndex];
        if (!thisLinePoint) {
            return;
        }

        if (markerId === 'start-marker') {
            if (this._mapModel.startPointChanged) {
                this._mapModel.startPointChanged(coordinates);
            }
        }

        // we are either already set, or need to fill with snap to roads points
        if ((this.isMapPlannerRouting(this._model.routingService) ? markerId === 'step-marker' : true) && this._model.snapToRoads && this._mapModel.linePoints.length > 1 && thisLinePoint) {
            // nothing before starting point / nothing before a resume point
            // nothing after end point / nothing after a pause point
            // otherwise, use this point as waypoint

            var startingPoint;
            var endingPoint;
            var waypoint;
            var callback;
            if (linePointIndex === 0 || thisLinePoint.deltaPause > 0) {
                // this is a start or resume point, only show stuff after
                startingPoint = thisLinePoint.latLng;
                if (this._mapModel.linePoints[markerProperties.nextKeyPointIndex]) {
                    endingPoint = this._mapModel.linePoints[markerProperties.nextKeyPointIndex].latLng;
                }
                waypoint = null;
                callback = (pointArray) => {
                    if (typeof pointArray !== 'undefined') {
                        var points = [];
                        for (var routePointIndex = 0; routePointIndex < pointArray.length; routePointIndex++) {
                            points.push(new MapInterface.PointInfo(pointArray[routePointIndex], null, {
                                computed: true,
                                surface: pointArray[routePointIndex].surface
                            }));
                        }

                        for (var j = 0; j < points.length; j++) {
                            this._mapModel.linePoints.splice(linePointIndex + 1 + j, 0, points[j]);
                        }
                    }

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();

                    if (this.hasModule('ISOCHRONE') && this.computeIsochrone && linePointIndex === 0) {
                        this.computeIsochrone(coordinates);
                    }
                };
            } else if (linePointIndex === this._mapModel.linePoints.length - 1 ||
                this._mapModel.linePoints[linePointIndex + 1].deltaPause > 0) {
                // this is a finish or pause point, only show stuff before
                if (this._mapModel.linePoints[markerProperties.prevKeyPointIndex]) {
                    startingPoint = this._mapModel.linePoints[markerProperties.prevKeyPointIndex].latLng;
                }
                endingPoint = thisLinePoint.latLng;
                waypoint = null;
                callback = (pointArray) => {
                    if (typeof pointArray !== 'undefined') {
                        var points = [];
                        for (var n = 0; n < pointArray.length; n++) {
                            points.push(new MapInterface.PointInfo(pointArray[n], null, {
                                computed: true,
                                surface: pointArray[n].surface
                            }));
                        }

                        for (var j = 0; j < points.length; j++) {
                            this._mapModel.linePoints.splice(markerProperties.prevKeyPointIndex + 1 + j, 0, points[j]);
                        }
                    }

                    markerProperties.linePointIndex = linePointIndex + 1;

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();
                };
            } else {
                // normal, move with waypoint
                if (this._mapModel.linePoints[markerProperties.prevKeyPointIndex]) {
                    startingPoint = this._mapModel.linePoints[markerProperties.prevKeyPointIndex].latLng;
                }
                if (this._mapModel.linePoints[markerProperties.nextKeyPointIndex]) {
                    endingPoint = this._mapModel.linePoints[markerProperties.nextKeyPointIndex].latLng;
                }
                waypoint = thisLinePoint.latLng;
                callback = (pointArray) => {
                    if (typeof pointArray !== 'undefined') {
                        var createdReplacementPoint = false;
                        var wasCreatedReplacementPoint = false;
                        for (var routePointIndex = 0; routePointIndex < pointArray.length; routePointIndex++) {
                            var numberToOverwrite = 0;
                            var newKeypoint = null;
                            var newLinePointIndex = markerProperties.prevKeyPointIndex + 1 + routePointIndex;
                            if (routePointIndex > (pointArray.length / 2) && !createdReplacementPoint) {
                                newKeypoint = {
                                    type: 'Feature',
                                    properties: {
                                        linePointIndex: newLinePointIndex,
                                        draggable: this._mapModel.editMode
                                    },
                                    geometry: {
                                        type: 'Point',
                                        coordinates: [pointArray[routePointIndex].lng, pointArray[routePointIndex].lat]
                                    }
                                };

                                createdReplacementPoint = true;
                            }
                            if (routePointIndex === pointArray.length - 1) {
                                numberToOverwrite = 1;
                                // newLinePointIndex marker automatically removed on next redraw
                            }
                            var isComputed = true;
                            if (createdReplacementPoint && !wasCreatedReplacementPoint) {
                                isComputed = false;
                                wasCreatedReplacementPoint = true;
                            }
                            this._mapModel.linePoints.splice(newLinePointIndex, numberToOverwrite, new MapInterface.PointInfo(pointArray[routePointIndex], newKeypoint, {
                                computed: isComputed,
                                surface: pointArray[routePointIndex].surface
                            }));
                        }
                    }

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();
                };
            }

            this.getSnapToRoadsRoute(
                startingPoint,
                endingPoint,
                waypoint,
                callback,
                {
                    limitDistance: true,
                    distance: this._mapModel.distance
                }
            );
        } else {
            this._redrawKeyPoints();
            this._mapEditedFuncs();
        }
    };

    _getNextKeyPointIndex = (fromIndex, ignoreComputed) => {
        // discover index of next keypoint
        for (var linePointIndex = fromIndex + 1; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            var thisLinePoint = this._mapModel.linePoints[linePointIndex];
            if (thisLinePoint && (ignoreComputed || !thisLinePoint.computed)) {
                return linePointIndex;
            }
        }

        return fromIndex;
    };

    _getPrevKeyPointIndex = (fromIndex, ignoreComputed) => {
        // discover index of next keypoint
        if (fromIndex !== 0) {
            for (var linePointIndex = fromIndex - 1; linePointIndex >= 0; linePointIndex--) {
                var thisLinePoint = this._mapModel.linePoints[linePointIndex];
                if (thisLinePoint && (ignoreComputed || !thisLinePoint.computed)) {
                    return linePointIndex;
                }
            }
        }

        return fromIndex;
    };

    _clearKeyPoints = (options) => {
        options ||= {};

        for (var linePointIndex = 0; linePointIndex < this._mapModel.linePoints.length; linePointIndex++) {
            if (options.keepStartStop) {
                if (linePointIndex === 0 || linePointIndex === this._mapModel.linePoints.length - 1) {
                    continue;
                }
            }

            var thisLinePoint = this._mapModel.linePoints[linePointIndex];
            if (thisLinePoint && thisLinePoint.keyPoint !== null) {
                thisLinePoint.keyPoint = null;
            }
        }

        if (options.clearAll) {
            this.resetMapSource('arrow-marker');
            this.resetMapSource('ghost-marker');
            this.resetMapSource('step-marker');
            this.resetMapSource('pause-marker');
            this.resetMapSource('resume-marker');

            this.resetMapSource('start-marker');
            this.resetMapSource('finish-marker');
        }
    };

    _addKeyPointAtClickLoc = (coordinates) => {
        // In POI/waypoint mode, a map click adds a point of interest, not a route point
        if (this._model.poiModeEnabled || this._model.waypointModeEnabled) {
            return;
        }

        // In freehand draw mode a stroke handles the trace; ignore the residual click on release
        if (this._model.freehandDraw) {
            return;
        }

        // In section-colour mode a click colours a leg, it must not add a route point
        if (this._model.sectionColorMode) {
            return;
        }

        // In retrace mode a click picks the turn-around point, not a new route point
        if (this._model.retraceMode) {
            return;
        }

        // Prevent bubbling click on marker and on map
        if (this._mapModel.markerEventClicked) {
            this._mapModel.markerEventClicked = false;
            return;
        }

        if (this._model.searchMarker) {
            this._model.searchMarker.remove();
        }

        if (this._mapModel.hasPopup) {
            this._mapModel.hasPopup = false;
            return;
        }

        // ensure that we aren't in the process of dragging a point or computing a path
        if (this._model.isComputingRoad) {
            return;
        }

        // Setup a snapshot to revert to
        this._pushUndoSnapShot();

        // create new point, add to map, redraw the trip line

        // we don't want to use the routing api if it's only the first point on the map (there's no beginning / end)
        var firstPoint = false;
        if (this._mapModel.linePoints.length === 0) {
            firstPoint = true;
        }
        var secondPoint = false;
        if (this._mapModel.linePoints.length === 1) {
            secondPoint = true;
        }

        if (firstPoint) {
            if (this._mapModel.startPointChanged) {
                this._mapModel.startPointChanged(coordinates);
            }
        }

        if (this.hasModule('TOOLBAR') && this.activateToolbarButtons) {
            if (firstPoint) {
                this.activateToolbarButtons();
            } else {
                this.activateBackToHome();
            }
        }

        if (this.hasModule('ISOCHRONE') && this.computeIsochrone && firstPoint) {
            this.computeIsochrone(coordinates);
        }

        // On the home the onboarding tour carries these hints; elsewhere surface them as
        // transient notifications on the first/second point.
        if (this._model.mapId !== 'home-map') {
            if (firstPoint) {
                Notification.info(window.screen.width < 1024
                    ? I18n.t('js.maps.helpers.mobile.draw_route')
                    : I18n.t('js.maps.helpers.desktop.draw_route'));
            } else if (secondPoint) {
                Notification.info(window.screen.width < 1024
                    ? I18n.t('js.maps.helpers.mobile.remove_point')
                    : I18n.t('js.maps.helpers.desktop.remove_point'));
            }
        }

        // need to remember where in the linePoints array we're adding to, so we can prep the undo function
        // var prevEndingIndex = this._mapModel.linePoints.length;

        // create any points involved, add to linePoints array. Guard on the COMPUTE_ROAD method being
        // present: if that module failed to load, fall back to a straight segment instead of crashing.
        if (this._model.snapToRoads && !firstPoint && typeof this.getSnapToRoadsRoute === 'function') {
            this.getSnapToRoadsRoute(
                this._mapModel.linePoints[this._mapModel.linePoints.length - 1].latLng,
                coordinates,
                null,
                (pointArray) => {
                    if (Array.isArray(pointArray)) {
                        for (let pointIndex = 0; pointIndex < pointArray.length; pointIndex++) {
                            this._mapModel.linePoints.push(new MapInterface.PointInfo(pointArray[pointIndex], null, {
                                computed: pointArray.length - 1 !== pointIndex,
                                surface: pointArray[pointIndex].surface
                            }));
                        }
                    } else {
                        // Error: just display a direct line
                        this._mapModel.linePoints.push(new MapInterface.PointInfo(coordinates, null, {computed: false}));
                    }

                    this._redrawKeyPoints();
                    this._mapEditedFuncs();

                    if (this.hasModule('RIDE_STORE') && this.storeRide) {
                        this.storeRide(this._mapModel.linePoints);
                    }
                },
                {
                    limitDistance: true,
                    distance: this._mapModel.distance
                }
            );
        } else {
            this._mapModel.linePoints.push(new MapInterface.PointInfo({
                lat: parseFloat(coordinates.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                lng: parseFloat(coordinates.lng.toFixed(MapInterface.COORDINATES_PRECISION))
            }, null, {computed: false}));
            this._redrawKeyPoints();
            this._mapEditedFuncs();

            if (this.hasModule('RIDE_STORE') && this.storeRide) {
                this.storeRide(this._mapModel.linePoints);
            }
        }
    };

    // Deep copy of the current line points (used for undo/redo snapshots).
    _cloneLinePoints = () => {
        const clone = [];
        for (let i = 0; i < this._mapModel.linePoints.length; i++) {
            const point = this._mapModel.linePoints[i];
            clone.push(new MapInterface.PointInfo(
                point.latLng,
                null,
                {
                    computed: point.computed,
                    deltaDistance: point.deltaDistance,
                    deltaTime: point.deltaTime,
                    deltaPause: point.deltaPause,
                    datetime: point.datetime,
                    elevation: point.elevation,
                    speed: point.speed,
                    heartRate: point.heartRate
                }
            ));
        }
        return clone;
    };

    _pushUndoSnapShot = () => {
        // Any structural edit invalidates the nudge selection (indices move). Nudge restores its
        // own selection right after taking its snapshot.
        this._mapModel.selectedKeypointIndex = null;

        this._mapModel.snapShotStack.push(this._cloneLinePoints());

        // Record the route edit in the shared action history so undo() removes actions in true recency
        // order (a route edit made after a POI must be undone before that POI). Kept 1:1 with
        // snapShotStack — one 'route' marker per snapshot, popped together on undo/redo.
        (this._mapModel.actionHistory ||= []).push('route');

        // A fresh edit invalidates the redo history.
        this._mapModel.redoStack = [];
    };

    // Restore the last state undone by undo(). Current state is pushed back onto the undo stack so
    // the redo can itself be undone.
    redo = () => {
        if (!this._mapModel.redoStack.length) {
            return;
        }

        this._mapModel.snapShotStack.push(this._cloneLinePoints());
        // Re-record the route action (undo popped it) so a later undo sees this redo as the newest edit.
        (this._mapModel.actionHistory ||= []).push('route');
        this._mapModel.selectedKeypointIndex = null;

        this._clearKeyPoints({clearAll: true});
        this._mapModel.linePoints = this._mapModel.redoStack.pop();
        this._redrawKeyPoints();

        if (!this._mapModel.linePoints.length) {
            this.resetMapSource('start-marker');
        } else if (this._mapModel.linePoints.length === 1) {
            this._redrawTripLine();
        }

        this._mapEditedFuncs();

        if (this._mapModel.distanceUpdated) {
            this._mapModel.distanceUpdated(this._mapModel.distance);
        }

        if (this.hasModule('TOOLBAR') && this.activateToolbarButtons && this.hasActivityData()) {
            this.activateToolbarButtons();
        }

        if (this.hasModule('RIDE_STORE') && this.storeRide) {
            this.storeRide(this._mapModel.linePoints);
        }
    };

    canRedo = () => {
        return this._mapModel.redoStack.length > 0;
    };

    _revertToSnapshot = (snapShotIndex) => {
        var i;
        var numberToPop;

        if (typeof snapShotIndex === 'undefined' || snapShotIndex === undefined || snapShotIndex === null) {
            snapShotIndex = this._mapModel.snapShotStack.length - 1;
        }

        if (snapShotIndex < 0) {
            return;
        }

        this._clearKeyPoints({clearAll: true});

        this._mapModel.linePoints = this._mapModel.snapShotStack[snapShotIndex] || [];

        numberToPop = this._mapModel.snapShotStack.length - snapShotIndex;
        for (i = 0; i < numberToPop; i++) {
            this._mapModel.snapShotStack.pop();
        }

        this._redrawKeyPoints();

        if (!this._mapModel.linePoints.length) {
            this.resetMapSource('start-marker');
        } else if (this._mapModel.linePoints.length === 1) {
            this._redrawTripLine();
        }

        this._mapEditedFuncs();
    };

    _mapEditedFuncs = () => {
        for (var i = 0, n = this._mapModel.mapEdited.length; i < n; i++) {
            this._mapModel.mapEdited[i](); //let callback know
        }
    };

    _sectionClickedFuncs = (payload) => {
        for (var i = 0, n = this._mapModel.sectionClicked.length; i < n; i++) {
            this._mapModel.sectionClicked[i](payload);
        }
    };

    /* Unused since the retrace tool was removed — kept for a quick re-enable.
    _retraceDoneFuncs = () => {
        for (var i = 0, n = this._mapModel.retraceDone.length; i < n; i++) {
            this._mapModel.retraceDone[i]();
        }
    };
    */

    _persistSections = () => {
        if (this.hasModule('RIDE_STORE') && this.storeSections) {
            this.storeSections(this._mapModel.sections);
        }
    };

    _reapplySectionColors = () => {
        if (!this._mapModel.sections.length) {
            // No sections left: revert to the flat colour and restore the plain LineString source.
            this.setSectionColorMode(this._model.mapTrackLineName, false);
            this._redrawTripLine();

            return;
        }

        this.applySectionColorsIfEnabled();
    };

    _nearestLinePointIndex = (lngLat) => {
        if (!this._mapModel.linePoints.length) {
            return null;
        }

        var bestIndex = 0;
        var bestDistance = Infinity;
        for (var i = 0; i < this._mapModel.linePoints.length; i++) {
            var point = this._mapModel.linePoints[i].latLng;
            var distance = (point.lat - lngLat.lat) ** 2 + (point.lng - lngLat.lng) ** 2;
            if (distance < bestDistance) {
                bestDistance = distance;
                bestIndex = i;
            }
        }

        return bestIndex;
    };

    _enclosingManualRange = (index) => {
        var points = this._mapModel.linePoints;
        if (points.length < 2) {
            return null;
        }

        var from = index;
        while (from > 0 && points[from].computed) {
            from--;
        }

        var to = index;
        while (to < points.length - 1 && points[to].computed) {
            to++;
        }

        if (to === from) {
            if (from < points.length - 1) {
                to = from + 1;
                while (to < points.length - 1 && points[to].computed) {
                    to++;
                }
            } else if (from > 0) {
                from -= 1;
                while (from > 0 && points[from].computed) {
                    from--;
                }
            }
        }

        return to > from ? {
            from,
            to
        } : null;
    };

    _sectionColorAt = (from, to) => {
        var existing = this._mapModel.sections.find((section) => section.from < to && section.to > from);

        return existing ? existing.color : null;
    };

    _fitMapBounds = (options = {}) => {
        if (!this._model.map) {
            return;
        }

        // get the map bounds by looking at all polylines involved
        if (this._model.mapBounds !== null) {
            return;
        }

        var bounds = this._buildBounds();

        // Fit to polylines or clusters
        if (this._mapModel.linePoints.length > 0) {
            this._mapModel.linePoints.forEach((point) => {
                bounds = bounds.extend([point.latLng.lng, point.latLng.lat]);
            });
        }

        if (Utils.isPresent(this._model.includeBounds) && this._model.includeBounds.length === 4) {
            var bbox = [
                [
                    parseFloat(this._model.includeBounds[1]),
                    parseFloat(this._model.includeBounds[0])
                ],
                [
                    parseFloat(this._model.includeBounds[3]),
                    parseFloat(this._model.includeBounds[2])
                ]
            ];
            bounds = bounds.extend(bbox);
        }

        if (this.hasModule('RIDE_POIS') && this.fitRidePoisBounds) {
            this.fitRidePoisBounds(bounds);
        }

        // fit to the overall bounds
        var fitBoundsOptions = {
            // Prevent map zoom on initial map loading
            zoom: this._model.minZoom,
            padding: this._mapModel.initialPoints || options.withPois ? 150 : 20
        };

        this._model.mapBounds = bounds;
        this.fitBounds(this._model.mapBounds, fitBoundsOptions);

        // Set minimum zoom
        if (this._model.map.getZoom() > (this._model.minZoom || 16)) {
            this._model.map.setZoom(this._model.minZoom || 16);
        }
    };

    // Called by MapInterface
    _onMapStyleChanged = () => {
        this.addMapTrackLine(this._model.mapTrackLineName, {offRoadOverlay: true});

        // this._redrawTripLine();

        this._redrawKeyPoints();

        // this._drawDirectionArrows();
    };

    // Build the overlay geometry for freehand segments kept off-road (not matched to a road),
    // grouping consecutive off-road legs into polylines for clean dashed rendering.
    _buildOffRoadGeoJSON = () => {
        const features = [];
        const points = this._mapModel.linePoints;
        let run = null;

        for (let i = 1; i < points.length; i++) {
            if (points[i].offRoad) {
                if (!run) {
                    run = [[points[i - 1].latLng.lng, points[i - 1].latLng.lat]];
                }
                run.push([points[i].latLng.lng, points[i].latLng.lat]);
            } else if (run) {
                features.push({
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: run
                    }
                });
                run = null;
            }
        }

        if (run) {
            features.push({
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: run
                }
            });
        }

        return {
            type: 'FeatureCollection',
            features
        };
    };
}
